import React, { useState } from 'react';

import PropTypes from 'prop-types';

import clsx from 'clsx';

import { useDevice } from '@bonnet/next/device';

import {
    Box,
    Button,
    Collapsible,
    Glyphicon,
    Image,
    Link,
    SubHeading,
} from 'reaxl';

function CollapsibleToggle({ collapsed, onExpand, inverse }) {
    return (
        <Button
            bsStyle={inverse ? 'text-inverse' : 'text'}
            onClick={!collapsed ? onExpand : undefined}
            className="display-flex align-items-center"
        >
            {collapsed ? 'See Less' : 'See More'}
            <Glyphicon
                className="margin-left-1 text-size-500"
                glyph={collapsed ? 'menu-up' : 'menu-down'}
            />
        </Button>
    );
}

function Card({
    children,
    collapsible,
    handleClick,
    heading,
    id,
    image,
    imageProps = {},
    link,
    linkLabel,
    showTile,
    summary,
    onExpand = () => { },
    isInverse,
    ...rest
}) {
    const device = useDevice();

    const [showMoreContent, setShowMoreContent] = useState(false);

    const handleCollapsibleHeaderToggle = () => setShowMoreContent((prevState) => !prevState);

    if (!summary) {
        summary = children;
        children = null;
    }

    const logoComponent = showTile && (
        <div
            className={clsx({ 'align-self-center': !isInverse })}
            key={`image-${id}`}
        >
            {link ? (
                <Link
                    href={link}
                    onClick={handleClick || undefined}
                    target="_blank"
                    rel="nofollow noopener"
                    aria-label={linkLabel || heading}
                >
                    <Image
                        alt=""
                        src={image}
                        width={imageProps.width || 58}
                        height={imageProps.height || 58}
                    />
                </Link>
            ) : (
                <Image
                    alt=""
                    aria-hidden="true"
                    src={image}
                    onClick={handleClick}
                    width={imageProps.width || 58}
                    height={imageProps.height || 58}
                />
            )}
        </div>
    );

    const mainContent = isInverse ? (
        <div className="display-flex gap-5">
            {logoComponent}
            <div>
                <SubHeading
                    level={3}
                    className="margin-bottom-2"
                >
                    {heading}
                </SubHeading>
                {summary}
            </div>
        </div>
    ) : (
        <>
            <SubHeading size={400}>
                {heading}
            </SubHeading>
            <div className="display-flex justify-content-between">
                <div className="padding-right-xs-6 padding-right-sm-2 padding-right-md-5 padding-right-lg-6">
                    {summary}
                </div>
                {logoComponent}
            </div>
        </>
    );

    return (
        <Box
            key={id}
            data-cmp={`${id}Card`}
            {...(isInverse && {
                backgroundColor: 'neutral-primary-transparent-10',
                textColor: 'inverse',
            })}
            {...rest}
        >
            {!children && mainContent}
            {!!children && (
                <>
                    {mainContent}
                    {collapsible && device?.is?.xs ? (
                        <Collapsible
                            onToggle={handleCollapsibleHeaderToggle}
                            toggleFooter={(
                                <CollapsibleToggle
                                    collapsed={showMoreContent}
                                    onExpand={onExpand}
                                    inverse={isInverse}
                                />
                            )}
                        >
                            {children}
                        </Collapsible>

                    ) : children}
                </>
            )}
        </Box>
    );
}

Card.propTypes = {
    isInverse: PropTypes.bool,
};

export default Card;
